/**
 * SelectionBox - Seleção por área (rubber-band) estilo Google Drive
 * 
 * Permite clicar e arrastar o mouse para selecionar múltiplos itens.
 * A caixa azul semitransparente aparece durante a seleção.
 * Itens cujo bounding-box intersecta a caixa são selecionados.
 * 
 * Performance: mousemove/mouseup só são registrados durante arrastar ativo.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Box, alpha } from '@mui/material'

interface Rect {
  x: number
  y: number
  width: number
  height: number
}

interface Props {
  /** Ref do container onde a seleção deve funcionar */
  containerRef: React.RefObject<HTMLElement>
  /** Seletor CSS dos itens selecionáveis */
  selectableSelector: string
  /** Atributo data-* que contém o ID do item */
  idAttribute?: string
  /** Callback quando a seleção muda */
  onSelectionChange: (selectedIds: string[]) => void
  /** Se está habilitado */
  enabled?: boolean
  children: React.ReactNode
}

function rectsIntersect(a: Rect, b: Rect): boolean {
  return !(
    a.x + a.width < b.x ||
    b.x + b.width < a.x ||
    a.y + a.height < b.y ||
    b.y + b.height < a.y
  )
}

export default function SelectionBox({
  containerRef,
  selectableSelector,
  idAttribute = 'data-select-id',
  onSelectionChange,
  enabled = true,
  children
}: Props) {
  const [selectionRect, setSelectionRect] = useState<Rect | null>(null)
  const startPointRef = useRef<{ x: number; y: number } | null>(null)
  const animFrameRef = useRef<number>(0)
  const isSelectingRef = useRef(false)
  const onSelectionChangeRef = useRef(onSelectionChange)
  onSelectionChangeRef.current = onSelectionChange

  const getContainerOffset = useCallback(() => {
    if (!containerRef.current) return { x: 0, y: 0 }
    const rect = containerRef.current.getBoundingClientRect()
    return { x: rect.left, y: rect.top }
  }, [containerRef])

  // mousemove e mouseup são registrados somente ao iniciar arraste
  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!startPointRef.current || !containerRef.current) return

    cancelAnimationFrame(animFrameRef.current)
    animFrameRef.current = requestAnimationFrame(() => {
      if (!startPointRef.current || !containerRef.current) return

      const offset = getContainerOffset()
      const scrollTop = containerRef.current.scrollTop || 0
      const scrollLeft = containerRef.current.scrollLeft || 0

      const currentX = e.clientX - offset.x + scrollLeft
      const currentY = e.clientY - offset.y + scrollTop

      const x = Math.min(startPointRef.current.x, currentX)
      const y = Math.min(startPointRef.current.y, currentY)
      const width = Math.abs(currentX - startPointRef.current.x)
      const height = Math.abs(currentY - startPointRef.current.y)

      // Só mostra a caixa se moveu mais de 5px (evita cliques simples)
      if (width < 5 && height < 5) return

      const rect: Rect = { x, y, width, height }
      setSelectionRect(rect)

      // Calcula quais itens estão intersectando
      const selectables = containerRef.current.querySelectorAll(selectableSelector)
      const selectedIds: string[] = []
      const containerOffset = getContainerOffset()
      const elScrollTop = containerRef.current.scrollTop || 0
      const elScrollLeft = containerRef.current.scrollLeft || 0

      selectables.forEach(el => {
        const id = el.getAttribute(idAttribute)
        if (!id) return

        const elRect = el.getBoundingClientRect()
        const itemRect: Rect = {
          x: elRect.left - containerOffset.x + elScrollLeft,
          y: elRect.top - containerOffset.y + elScrollTop,
          width: elRect.width,
          height: elRect.height
        }

        if (rectsIntersect(rect, itemRect)) {
          selectedIds.push(id)
        }
      })

      onSelectionChangeRef.current(selectedIds)
    })
  }, [containerRef, selectableSelector, idAttribute, getContainerOffset])

  const handleMouseUp = useCallback(() => {
    cancelAnimationFrame(animFrameRef.current)
    isSelectingRef.current = false
    setSelectionRect(null)
    startPointRef.current = null
    document.removeEventListener('mousemove', handleMouseMove)
    document.removeEventListener('mouseup', handleMouseUp)
  }, [handleMouseMove])

  const handleMouseDown = useCallback((e: MouseEvent) => {
    if (!enabled || !containerRef.current) return

    // Ignora se o clique foi em um elemento interativo
    const target = e.target as HTMLElement
    if (
      target.closest('button') ||
      target.closest('input') ||
      target.closest('a') ||
      target.closest('[role="menu"]') ||
      target.closest('[role="dialog"]') ||
      target.closest('.MuiCheckbox-root') ||
      target.closest('.MuiIconButton-root') ||
      target.closest('.MuiMenu-root') ||
      target.closest('.MuiModal-root') ||
      target.tagName === 'INPUT' ||
      target.tagName === 'BUTTON'
    ) {
      return
    }

    // Só inicia se clicar no container ou filhos diretos (não em cards)
    const clickedOnCard = target.closest(selectableSelector)
    if (clickedOnCard) return

    e.preventDefault()
    const offset = getContainerOffset()
    const scrollTop = containerRef.current.scrollTop || 0
    const scrollLeft = containerRef.current.scrollLeft || 0

    startPointRef.current = {
      x: e.clientX - offset.x + scrollLeft,
      y: e.clientY - offset.y + scrollTop
    }
    isSelectingRef.current = true
    setSelectionRect(null)

    // Registra mousemove/mouseup SOMENTE durante o arraste
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
  }, [enabled, containerRef, selectableSelector, getContainerOffset, handleMouseMove, handleMouseUp])

  useEffect(() => {
    if (!enabled) return

    document.addEventListener('mousedown', handleMouseDown)

    return () => {
      document.removeEventListener('mousedown', handleMouseDown)
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      cancelAnimationFrame(animFrameRef.current)
    }
  }, [enabled, handleMouseDown, handleMouseMove, handleMouseUp])

  return (
    <Box sx={{ position: 'relative' }}>
      {children}

      {/* Caixa de seleção visual */}
      {selectionRect && (
        <Box
          sx={{
            position: 'absolute',
            left: selectionRect.x,
            top: selectionRect.y,
            width: selectionRect.width,
            height: selectionRect.height,
            bgcolor: alpha('#1976d2', 0.08),
            border: `2px solid ${alpha('#1976d2', 0.4)}`,
            borderRadius: 0.5,
            pointerEvents: 'none',
            zIndex: 50,
            transition: 'none'
          }}
        />
      )}
    </Box>
  )
}
